package org.catharsis.TVTally

import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.navigation.NavHostController
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.rememberNavController
import androidx.navigation.toRoute
import kotlinx.coroutines.flow.collectLatest
import org.catharsis.TVTally.theme.DarkColorScheme
import org.catharsis.TVTally.theme.LightColorScheme
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.core.navigation.ExpenseReportRoute
import org.catharsis.spendtel.core.navigation.PublicShareRoute
import org.catharsis.spendtel.core.navigation.consumeInitialPublicShareRoute
import org.catharsis.spendtel.core.navigation.consumeInitialPublicWebRoute
import org.catharsis.spendtel.core.navigation.resolveAppStartDestination
import org.catharsis.spendtel.core.navigation.showsWebLandingPage
import org.catharsis.spendtel.core.platform.clearPurchaseReturnQuery
import org.catharsis.spendtel.core.platform.consumePurchaseReturnQuery
import org.catharsis.spendtel.core.platform.syncRevenueCatUser
import org.catharsis.spendtel.core.platform.syncRevenueCatUserLoggedOut
import org.catharsis.spendtel.core.platform.usesNativeStoreBilling
import org.catharsis.spendtel.feature.data.ScreenName
import org.catharsis.spendtel.feature.presentation.ExpenseReportScreen
import org.catharsis.spendtel.feature.presentation.HomeScreen
import org.catharsis.spendtel.feature.presentation.HomeEvent
import org.catharsis.spendtel.feature.presentation.HomeViewModel
import org.catharsis.spendtel.feature.presentation.SpendTelViewModel
import org.catharsis.spendtel.feature.presentation.auth.AuthenticationScreen
import org.catharsis.spendtel.feature.presentation.auth.AuthenticationViewModel
import org.catharsis.spendtel.feature.presentation.financialgoal.FinancialGoalSelectionScreen
import org.catharsis.spendtel.feature.presentation.legal.LegalDocumentScreen
import org.catharsis.spendtel.feature.presentation.settings.SettingsScreen
import org.catharsis.spendtel.feature.presentation.support.ContactSupportScreen
import org.catharsis.spendtel.feature.legal.LegalDocumentType
import org.catharsis.spendtel.feature.presentation.publicshare.PlatformPublicShareScreen
import org.catharsis.spendtel.feature.presentation.web.LandingScreen
import org.catharsis.spendtel.ui.AppLoadingScreen
import org.catharsis.spendtel.ui.components.SpotlightTooltipProvider
import org.catharsis.spendtel.ui.components.rememberSpotlightState
import org.koin.compose.KoinContext
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun MyApp() {
    AppScaffold {
        App()
    }
}

@Composable
fun App(
    navController: NavHostController = rememberNavController(),
) {
    val viewModel = koinViewModel<SpendTelViewModel>()
    val authViewModel = koinViewModel<AuthenticationViewModel>()
    val homeViewModel = koinViewModel<HomeViewModel>()
    val authState by authViewModel.authState.collectAsState()

    PlatformAppEffects(viewModel)

    LaunchedEffect(authState.isInitialized, authState.isAuthenticated, authState.currentUser?.uid) {
        if (!authState.isInitialized || !usesNativeStoreBilling()) return@LaunchedEffect
        if (authState.isAuthenticated) {
            authState.currentUser?.uid?.let { syncRevenueCatUser(it) }
        } else {
            syncRevenueCatUserLoggedOut()
        }
    }

    LaunchedEffect(authState.isInitialized, authState.isAuthenticated) {
        if (!authState.isInitialized || !authState.isAuthenticated) return@LaunchedEffect
        consumePurchaseReturnQuery()?.let { returnType ->
            clearPurchaseReturnQuery()
            homeViewModel.postEvent(HomeEvent.OnPurchaseReturn(returnType))
        }
    }

    var darkModeOn = false
    val colors = if (darkModeOn) DarkColorScheme else LightColorScheme
    val currentColorScheme = remember { mutableStateOf(colors) }
    LaunchedEffect(authState.isAuthenticated, authState.currentUser?.uid) {
        val uid = authState.currentUser?.uid ?: return@LaunchedEffect
        viewModel.listenToUserChanges(uid).collectLatest { updatedUser ->
            darkModeOn = updatedUser?.darkModeActive == true
            currentColorScheme.value = if (darkModeOn) DarkColorScheme else LightColorScheme
        }
    }

    MaterialTheme(
        colorScheme = currentColorScheme.value,
    ) {
        KoinContext {
            ConfigureImageLoader()

            val spotlightState = rememberSpotlightState()
            SpotlightTooltipProvider(state = spotlightState) {
                val initialPublicWebRoute = remember { consumeInitialPublicWebRoute() }
                val startDestination = initialPublicWebRoute ?: resolveAppStartDestination(
                    isAuthenticated = authState.isAuthenticated,
                    user = authState.currentUser,
                )
                val initialPublicShare = remember { consumeInitialPublicShareRoute() }

                LaunchedEffect(authState.isInitialized, initialPublicShare, startDestination) {
                    if (!authState.isInitialized) return@LaunchedEffect
                    initialPublicShare?.let { route ->
                        navController.navigate(route) {
                            popUpTo(startDestination) { inclusive = false }
                            launchSingleTop = true
                        }
                    }
                }

                if (authState.isInitialized) {
                    NavHost(
                        navController = navController,
                        startDestination = startDestination,
                        modifier = Modifier.fillMaxSize(),
                        enterTransition = { EnterTransition.None },
                        exitTransition = { ExitTransition.None },
                        popEnterTransition = { EnterTransition.None },
                        popExitTransition = { ExitTransition.None },
                    ) {
                        if (showsWebLandingPage()) {
                            composable(AppRoutes.Landing.name) {
                                viewModel.logScreenViewed(ScreenName.LANDING_SCREEN)
                                LandingScreen(navController)
                            }
                        }

                        composable(AppRoutes.Authentication.name) {
                            viewModel.logScreenViewed(ScreenName.AUTHENTICATION_SCREEN)
                            AuthenticationScreen(navController)
                        }

                        composable(AppRoutes.FinancialGoal.name) {
                            viewModel.logScreenViewed(ScreenName.FINANCIAL_GOAL_SCREEN)
                            FinancialGoalSelectionScreen(navController)
                        }

                        composable(AppRoutes.EditFinancialGoal.name) {
                            viewModel.logScreenViewed(ScreenName.FINANCIAL_GOAL_SCREEN)
                            FinancialGoalSelectionScreen(
                                navHostController = navController,
                                isEditMode = true,
                            )
                        }

                        composable(AppRoutes.Settings.name) {
                            viewModel.logScreenViewed(ScreenName.SETTINGS_SCREEN)
                            SettingsScreen(navController)
                        }

                        composable(AppRoutes.PrivacyPolicy.name) {
                            viewModel.logScreenViewed(ScreenName.PRIVACY_POLICY_SCREEN)
                            LegalDocumentScreen(
                                navHostController = navController,
                                documentType = LegalDocumentType.PrivacyPolicy,
                            )
                        }

                        composable(AppRoutes.TermsAndConditions.name) {
                            viewModel.logScreenViewed(ScreenName.TERMS_AND_CONDITIONS_SCREEN)
                            LegalDocumentScreen(
                                navHostController = navController,
                                documentType = LegalDocumentType.TermsAndConditions,
                            )
                        }

                        composable(AppRoutes.ContactSupport.name) {
                            viewModel.logScreenViewed(ScreenName.CONTACT_SUPPORT_SCREEN)
                            ContactSupportScreen(
                                navHostController = navController,
                                defaultName = authState.currentUser?.username,
                                defaultEmail = authState.currentUser?.emailAddress,
                            )
                        }

                        composable(AppRoutes.Home.name) {
                            viewModel.logScreenViewed(ScreenName.HOME_SCREEN)
                            HomeScreen(navController)
                        }

                        composable<ExpenseReportRoute> { backStackEntry ->
                            val route = backStackEntry.toRoute<ExpenseReportRoute>()
                            viewModel.logScreenViewed(ScreenName.EXPENSE_REPORT_SCREEN)
                            ExpenseReportScreen(
                                navHostController = navController,
                                reportId = route.reportId,
                            )
                        }

                        composable<PublicShareRoute> { backStackEntry ->
                            val route = backStackEntry.toRoute<PublicShareRoute>()
                            viewModel.logScreenViewed(ScreenName.PUBLIC_SHARE_SCREEN)
                            PlatformPublicShareScreen(shareId = route.shareId)
                        }
                    }
                } else {
                    AppLoadingScreen(currentColorScheme.value)
                }
            }
        }
    }
}
